import { useMemo } from 'react';
import PropTypes from 'prop-types';

/**
 * Connection Status Component for SOC Dashboard
 * Displays the current connection status and last refresh time with visual indicators
 * 
 * @param {Object} props - Component props
 * @param {string} props.status - Current connection status ('online', 'updating', 'offline')
 * @param {Date} props.lastRefreshTime - Timestamp of last successful refresh
 * @param {string} props.countdownDisplay - Countdown timer display string (MM:SS)
 * @param {boolean} props.isPaused - Whether auto-refresh is paused
 */
const ConnectionStatus = ({ status, lastRefreshTime, countdownDisplay, isPaused }) => {
  /**
   * Memoize the display time calculation
   */
  const displayTime = useMemo(() => {
    if (!lastRefreshTime) return '';
    return new Date(lastRefreshTime).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  }, [lastRefreshTime]);

  /**
   * Gets the status icon and color based on connection status
   */
  const getStatusIndicator = () => {
    switch (status) {
      case 'online':
        return {
          icon: '●',
          color: '#28a745',
          label: 'Connected',
          pulse: false
        };
      case 'updating':
        return {
          icon: '↻',
          color: '#ffc107',
          label: 'Updating...',
          pulse: true
        };
      case 'offline':
        return {
          icon: '●',
          color: '#dc3545',
          label: 'Connection Error',
          pulse: true
        };
      default:
        return {
          icon: '●',
          color: '#6c757d',
          label: 'Unknown',
          pulse: false
        };
    }
  };

  const statusIndicator = getStatusIndicator();

  return (
    <div className="connection-status">
      <div className="status-indicator-container">
        <span
          className={`status-icon ${statusIndicator.pulse ? 'pulse' : ''}`}
          style={{ color: statusIndicator.color }}
          aria-label={statusIndicator.label}
        >
          {statusIndicator.icon}
        </span>
        <span className="status-label">{statusIndicator.label}</span>
      </div>

      {!isPaused && status !== 'offline' && (
        <div className="refresh-info">
          <span className="refresh-countdown">
            Next refresh in: <strong>{countdownDisplay}</strong>
          </span>
        </div>
      )}

      {isPaused && (
        <div className="refresh-info">
          <span className="refresh-paused">Auto-refresh paused</span>
        </div>
      )}

      {displayTime && (
        <div className="last-refresh-time">
          Last updated: <strong>{displayTime}</strong>
        </div>
      )}
    </div>
  );
};

ConnectionStatus.propTypes = {
  status: PropTypes.oneOf(['online', 'updating', 'offline']).isRequired,
  lastRefreshTime: PropTypes.instanceOf(Date),
  countdownDisplay: PropTypes.string.isRequired,
  isPaused: PropTypes.bool.isRequired
};

ConnectionStatus.defaultProps = {
  lastRefreshTime: null
};

export default ConnectionStatus;
